import React, { useRef, useEffect } from 'react';
import { View } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Ionicons } from '@expo/vector-icons';
import { AuthProvider, useAuth } from '../context/AuthContext';
import { RideProvider, useRide, DRIVER_MODE } from '../context/RideContext';
import { notificationService } from '../services/notificationService';

// Import screens
import LoginScreen from '../screens/auth/LoginScreen';
import RegisterScreen from '../screens/auth/RegisterScreen';
import ForgotPasswordScreen from '../screens/auth/ForgotPasswordScreen';
import HomeScreen from '../screens/home/HomeScreen';
import EarningsScreen from '../screens/earnings/EarningsScreen';
import RouteMapScreen from '../screens/map/RouteMapScreen';
import DriverAccountScreen from '../screens/account/DriverAccountScreen';
import LoadingScreen from '../screens/LoadingScreen';

// Import components
import IncomingRideModal from '../components/IncomingRideModal';

const Stack = createStackNavigator();
const Tab = createBottomTabNavigator();

const MainTabNavigator = () => {
  const { driverMode } = useRide();
  const hideTabBar = driverMode === DRIVER_MODE.INCOMING || driverMode === DRIVER_MODE.ACTIVE;

  return (
    <Tab.Navigator
      screenOptions={({ route }) => ({
        tabBarIcon: ({ focused, color, size }) => {
          let iconName;

          if (route.name === 'Home') {
            iconName = focused ? 'home' : 'home-outline';
          } else if (route.name === 'Earnings') {
            iconName = focused ? 'wallet' : 'wallet-outline';
          } else if (route.name === 'Account') {
            iconName = focused ? 'person-circle' : 'person-circle-outline';
          }

          return <Ionicons name={iconName} size={size} color={color} />;
        },
        tabBarActiveTintColor: '#D4AF37',
        tabBarInactiveTintColor: '#666666',
        tabBarStyle: hideTabBar
          ? { display: 'none' }
          : {
              backgroundColor: '#111111',
              borderTopColor: '#222222',
            },
        headerStyle: {
          backgroundColor: '#000000',
        },
        headerTintColor: '#ffffff',
        headerTitleStyle: {
          fontWeight: 'bold',
        },
      })}
    >
      <Tab.Screen
        name="Home"
        component={HomeScreen}
        options={{ title: 'Obzidn Driver' }}
      />
      <Tab.Screen
        name="Earnings"
        component={EarningsScreen}
        options={{ title: 'Earnings & Wallet' }}
      />
      <Tab.Screen
        name="Account"
        component={DriverAccountScreen}
        options={{ title: 'Driver Account' }}
      />
    </Tab.Navigator>
  );
};

const AppContent = () => {
  const { user, isLoading } = useAuth();
  const navigationRef = useRef(null);

  useEffect(() => {
    if (user) {
      notificationService.registerForPushNotifications()
        .then(token => {
          if (token) console.log('[Driver] Push token registered');
        })
        .catch(err => console.warn('[Driver] Push registration failed:', err.message));
    }
  }, [user]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <View style={{ flex: 1 }}>
      <NavigationContainer ref={navigationRef}>
        <Stack.Navigator
          screenOptions={{
            headerStyle: { backgroundColor: '#000000' },
            headerTintColor: '#ffffff',
            headerTitleStyle: { fontWeight: 'bold' },
          }}
        >
          {user ? (
            <>
              <Stack.Screen
                name="MainTabs"
                component={MainTabNavigator}
                options={{ headerShown: false }}
              />
              <Stack.Screen
                name="RouteMap"
                component={RouteMapScreen}
                options={{ title: 'Navigation', presentation: 'modal' }}
              />
            </>
          ) : (
            <>
              <Stack.Screen
                name="Login"
                component={LoginScreen}
                options={{ headerShown: false }}
              />
              <Stack.Screen
                name="Register"
                component={RegisterScreen}
                options={{ headerShown: false }}
              />
              <Stack.Screen
                name="ForgotPassword"
                component={ForgotPasswordScreen}
                options={{ title: 'Reset Password' }}
              />
            </>
          )}
        </Stack.Navigator>
      </NavigationContainer>

      {/* Full-screen ride request overlay - renders above navigation */}
      {user && (
        <IncomingRideOverlay />
      )}
    </View>
  );
};

// Separate component so it can use useRide() inside RideProvider
// No navigation on accept — HomeScreen reacts to activeRide state directly
const IncomingRideOverlay = () => {
  return <IncomingRideModal onAccepted={() => {}} />;
};

const AppNavigator = () => {
  try {
    return (
      <AuthProvider>
        <RideProvider>
          <AppContent />
        </RideProvider>
      </AuthProvider>
    );
  } catch (error) {
    console.error('AppNavigator error:', error);
    return null;
  }
};

export default AppNavigator;
